/**
 * Client Configuration
 * Multi-tenant configuration for partner clients and collections
 * Each client gets their own route: /roq, /freedom-fighters, /pioneers, /aana
 */

import { ExperienceConfig } from './experience'
import { ExperienceItem } from '../types/experience'
import { CharacterConfig } from '../types/index'

export interface FeaturedConfig {
  title: string
  description: string
  logoSrc?: string
}

/**
 * Client configuration interface
 */
export interface FeaturedConfig {
  title: string
  description: string
  logoSrc?: string
  link: string
}



export interface ClientConfig {
  clientId: string
  clientName: string
  experienceConfig: ExperienceConfig
  experienceItem: ExperienceItem
  enabled: boolean
  characters?: CharacterConfig[] 
    featured?: FeaturedConfig
  
}


// ---------------------------------------------------------------------------
// AANA Collection
// ---------------------------------------------------------------------------
export const AANA_CLIENT: ClientConfig = {
  clientId: 'aana',
  clientName: 'AANA',
  enabled: true,
  experienceConfig: {
    experienceId: 'aana',
    clientName: 'AANA',
    ctaText: 'Visit Website',
    ctaUrl: 'https://www.aana.com', 
    triggerAssets: ['/assets/compiled-image-targets/aana.mind'],
    themeAccent: '#6c3483',
    aboutTitle: 'About AANA',
    aboutText: '', // Added here to satisfy the strict 'ExperienceConfig' requirement
    rockablockLink: 'https://rockablock.com'
  },
  experienceItem: {
    id: 'aana',
    title: 'American Association of Nurse Anesthesiology', 
    thumbnail: '/aana-featured-img.jpg',
    category: 'Brands',
    triggerImage: '/assets/compiled-image-targets/aana.mind',
    clientName: 'AANA',
    ctaUrl: 'https://www.aana.com', 
    ctaText: 'Visit Website',
    themeAccent: '#c33299', 
    isFeatured: true,
    
    subtitle: "Augmented reality meets the world's leading nurse anesthesia professionals.",
    aboutInnerImage: "/images/aana-image2.jpg",
    aboutImageOverlay: "Interact with nursing giants through out history by scanning the pins with ROCK AR",
    description: "The American Association of Nurse Anesthesiology (AANA) was founded in 1931 to support Certified Registered Nurse Anesthetists (CRNAs) and advance the practice of nurse anesthesiology. Since its founding, AANA has helped establish certification standards, support education and advocacy efforts, and improve patient access to anesthesia care across the United States.",
    collaborationText: "This exclusive collaboration with ROCK AR bridges physical design with digital reality, telling stories throughout the years of nurse anesthesiology with interactive augmented pins.",

    apparel: [
    { name: 'Goldie Brangman', image: '/images/Brangman_Trigger.png' },
    { name: 'John F. Garde', image: '/images/Garde_Trigger.png' },
    { name: 'Ira P. Gunn', image: '/images/ira-p-gunn.png' },
    ]
    
  },
  characters: [
    {
      targetIndex: 0,
      characterId: 'brangman',
      name: 'Brangman',
      videoUrl: '/assets/Animations/Brangman/Brangman-trans.webm',
      iosVideoUrl: '/assets/Animations/Brangman/NewBrangman.mov',
      chromeIOSVideoUrl: '/assets/Animations/Brangman/Converted_Brangman.mp4',
      audioUrl: '/assets/music/AANAMusic/brangman.m4a',
      thumbnail: '/images/Brangman_Trigger.png',
      mindFile: '/assets/compiled-image-targets/Brangman.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More'
    },
    {
      targetIndex: 1,
      characterId: 'garde',
      name: 'Garde',
      videoUrl: '/assets/Animations/Garde/GARDE-trans.webm',
      iosVideoUrl: '/assets/Animations/Garde/NewGARDE.mov',
      chromeIOSVideoUrl: '/assets/Animations/Garde/Converted_Garde.mp4',
      audioUrl: '/assets/music/AANAMusic/garde.m4a',
      thumbnail: '/images/Garde_Trigger.png',
      mindFile: '/assets/compiled-image-targets/Garde.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More'
    },
    {
      targetIndex: 2,
      characterId: 'gunn',
      name: 'Ira P. Gunn',
      videoUrl: '/assets/Animations/Gunn/gunn.webm',
      iosVideoUrl: '/assets/Animations/Gunn/green-gunn.mov',
      chromeIOSVideoUrl: '/assets/Animations/Gunn/green-gunn-h.264.mp4',
      audioUrl: '/assets/music/AANAMusic/gunn.m4a',
      thumbnail: '/images/ira-p-gunn.png',
      mindFile: '/assets/compiled-image-targets/Gunn.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More'
    }
  ],
    featured: {
    title: 'CRNA Focused, CRNA Inspired',
    description: 'The American Association of Nurse Anesthesiology represents 69,000+ nurses and...',
    logoSrc: '/aana-featured-img.jpg',
    link: 'https://www.aana.com'
  }
}

// ---------------------------------------------------------------------------
// ROQ Client
// ---------------------------------------------------------------------------
export const ROQ_CLIENT: ClientConfig = {
  clientId: 'roq',
  clientName: 'ROQ',
  enabled: true,
  experienceConfig: {
    experienceId: 'roq-ar-demo',
    clientName: 'ROQ',
    ctaText: '✨ ROQ — Learn More →',
    ctaUrl: 'https://www.roq.us/',
    ctaIcon: '✨',
    triggerAssets: ['/assets/compiled-image-targets/roq-target.mind'],
    themeAccent: '#ff086d',
    aboutText: '', 
    aboutTitle: 'About ROCK AR',
    rockablockLink: 'https://rockablock.com'
  },
  experienceItem: {
    id: 'roq-ar-demo',
    title: 'ROQ AR Experience',
    thumbnail: '/assets/logos/roq-logo.png',
    category: 'Brands',
    triggerImage: '/assets/compiled-image-targets/roq-target.mind',
    clientName: 'ROQ',
    ctaUrl: 'https://www.roq.us/',
    ctaText: 'Visit ROQ.us',
    themeAccent: '#ff086d',
    isFeatured: false,
    
    // New fields mapped for layout
    subtitle: 'Experience ROQ\'s innovative print designs through augmented reality.',
    description: 'ROCK AR brings custom graphic apparel into a new dimension. No app downloads required—just view through your default browser.',
    collaborationText: 'A creative collaboration highlighting cutting-edge web infrastructure paired directly with high-fidelity textile art.',
    stats: {
      scans: '820',
      activeUsers: '310',
      engagementRate: '76%'
    },
    apparel: [
    { name: 'T-Shirt', image: '/placeholder' },
    { name: 'Long Sleeve T-Shirt', image: '/placeholder' },
    ]
  }
}

// ---------------------------------------------------------------------------
// Freedom Fighters Collection
// ---------------------------------------------------------------------------
export const FREEDOM_FIGHTERS_CLIENT: ClientConfig = {
  clientId: 'freedom-fighters',
  clientName: 'Freedom Fighters',
  enabled: true,
  experienceConfig: {
    experienceId: 'freedom-fighters',
    clientName: 'Freedom Fighters',
    ctaText: 'Explore More',
    ctaUrl: 'https://www.rockablock.com/search?type=product%2Carticle%2Cpage&q=freedom+fighters',
    // Note: a combined 'freedom-fighters.mind' isn't present in /public.
    // Use an existing per-character .mind as a temporary default so MindAR can initialize.
    // Rebuild a combined multi-target .mind for full collection scanning when available.
      // Do not load a combined collection .mind by default — use the ROQ fallback
      // so individual character .mind files are only loaded when the user selects
      // a character (matches AANA per-character behavior).
      triggerAssets: ['/assets/compiled-image-targets/roq-target.mind'],
    themeAccent: '#8B0000',
    aboutText: '', 
    aboutTitle: 'Freedom Fighters AR',
    rockablockLink: 'https://rockablock.com'
  },
  experienceItem: {
    id: 'freedom-fighters',
    title: 'Freedom Fighters',
    thumbnail: '/images/freedom-fighters-thumbnail.png',
    category: 'Community Stories',
    triggerImage: '/assets/compiled-image-targets/freedom-fighters.mind',
    clientName: 'Freedom Fighters',
    ctaUrl: 'https://rockablock.com',
    ctaText: 'Explore More',
    themeAccent: '#8B0000',
    isFeatured: false,
    
    // New fields mapped for layout
    subtitle: 'Celebrating the lives and legacies of historical Black leaders who fought for freedom and justice.',
    description: 'The Freedom Fighters collection bridges education and contemporary streetwear design. Scan any layout to launch individual biographical timelines.',
    collaborationText: 'A powerful interactive gallery assignment curated in partnership with digital preservation archives.',
    stats: {
      scans: '2.4K',
      activeUsers: '1.1K',
      engagementRate: '91%'
    },
    apparel: [
    { name: 'Truth', image: '/assets/Images/FreedomFighters/Truth_trans.png' },
    { name: 'Garvey', image: '/assets/Images/FreedomFighters/garvey_trigger.png' },
    { name: 'Tubman', image: '/assets/Images/FreedomFighters/Harriet_trans.png' },
    { name: 'Douglass', image: '/assets/Images/FreedomFighters/douglass_trans.png' },
    { name: 'Wells', image: '/assets/Images/FreedomFighters/wells_trans.png' },
    { name: 'Du Bois', image: '/assets/Images/FreedomFighters/dubois_trans.png' }
    ]
  },
  characters: [
    {
      targetIndex: 0,
      characterId: 'douglass',
      name: 'Frederick Douglass',
      videoUrl: '/assets/Animations/Douglass/DOUGLAS.webm',
      iosVideoUrl: '/assets/Animations/Douglass/DOUGLAS-GREENSCREEN.mov',
      chromeIOSVideoUrl: '/assets/Animations/Douglass/DOUGLAS-greenscreen.mp4',
      audioUrl: '/assets/music/FreedomFighters/Douglass_Music.m4a',
      thumbnail: '/assets/Images/FreedomFighters/douglass_trans.png',
      mindFile: '/assets/compiled-image-targets/Douglass_comp.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'Abolitionist, author, and statesman'
    },
    {
      targetIndex: 1,
      characterId: 'dubois',
      name: 'W.E.B. Du Bois',
      videoUrl: '/assets/Animations/DuBois/DUBOIS.webm',
      iosVideoUrl: '/assets/Animations/DuBois/DUBOIS-GREENSCREEN.mov',
      chromeIOSVideoUrl: '/assets/Animations/DuBois/DUBOIS_greenscreen.mp4',
      audioUrl: '/assets/music/FreedomFighters/DuBois_Music.m4a',
      thumbnail: '/assets/Images/FreedomFighters/dubois_trans.png',
      mindFile: '/assets/compiled-image-targets/DuBois_comp.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'Scholar, civil rights activist, and co-founder of the NAACP'
    },
    {
      targetIndex: 2,
      characterId: 'garvey',
      name: 'Marcus Garvey',
      videoUrl: '/assets/Animations/Garvey/GARVEY.webm',
      iosVideoUrl: '/assets/Animations/Garvey/GARVEY-GREENSCREEN.mov',
      chromeIOSVideoUrl: '/assets/Animations/Garvey/GARVEY-greenscreen.mp4',
      audioUrl: '/assets/music/FreedomFighters/Garvey_Music.m4a',
      thumbnail: '/assets/Images/FreedomFighters/garvey_trigger.png',
      mindFile: '/assets/compiled-image-targets/Garvey_comp.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'Pan-Africanist and Black nationalist leader'
    },
    {
      targetIndex: 3,
      characterId: 'truth',
      name: 'Sojourner Truth',
      videoUrl: '/assets/Animations/Truth/TRUTH.webm',
      iosVideoUrl: '/assets/Animations/Truth/TRUTH-GREENSCREEN.mov',
      chromeIOSVideoUrl: '/assets/Animations/Truth/TRUTH-greenscreen.mp4',
      audioUrl: '/assets/music/FreedomFighters/Truth_Music.m4a',
      thumbnail: '/assets/Images/FreedomFighters/Truth_trans.png',
      mindFile: '/assets/compiled-image-targets/Truth_comp.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'Abolitionist and women\'s rights activist'
    },
    {
      targetIndex: 4,
      characterId: 'harriet',
      name: 'Harriet Tubman',
      videoUrl: '/assets/Animations/Harriet/TUBMAN_2.webm',
      iosVideoUrl: '/assets/Animations/Harriet/TUBMAN.mov',
      chromeIOSVideoUrl: '/assets/Animations/Harriet/TUBMAN_greenscreen.mp4',
      audioUrl: '/assets/music/FreedomFighters/Tubman_Music.m4a',
      thumbnail: '/assets/Images/FreedomFighters/Harriet_trans.png',
      mindFile: '/assets/compiled-image-targets/Tubman_comp.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'Underground Railroad conductor and Union spy'
    },
    {
      targetIndex: 5,
      characterId: 'wells',
      name: 'Ida B. Wells',
      videoUrl: '/assets/Animations/Wells/WELLS.webm',
      iosVideoUrl: '/assets/Animations/Wells/WELLS-GREENSCREEN.mov',
      chromeIOSVideoUrl: '/assets/Animations/Wells/WELLS_greenscreen.mp4',
      audioUrl: '/assets/music/FreedomFighters/Wells_Music.m4a',
      thumbnail: '/assets/Images/FreedomFighters/wells_trans.png',
      mindFile: '/assets/compiled-image-targets/Wells_comp.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'Journalist and anti-lynching activist'
    }
  ]
}

// ---------------------------------------------------------------------------
// Pioneers Collection
// ---------------------------------------------------------------------------
export const PIONEERS_CLIENT: ClientConfig = {
  clientId: 'pioneers',
  clientName: 'Pioneers',
  enabled: true,
  experienceConfig: {
    experienceId: 'pioneers',
    clientName: 'ROCKaBLOCK',
    ctaText: 'Explore More',
    ctaUrl: 'https://rockablock.com',
    triggerAssets: ['/assets/compiled-image-targets/pioneers.mind'],
    themeAccent: '#1a5276',
    aboutText: '', 
    aboutTitle: 'Pioneers AR',
    rockablockLink: 'https://rockablock.com'
  },
  experienceItem: {
    id: 'pioneers',
    title: 'Pioneers',
    thumbnail: '/images/the-pioneers.png',
    category: 'Community Stories',
    triggerImage: '/assets/compiled-image-targets/pioneers.mind',
    clientName: 'Pioneers',
    ctaUrl: 'https://rockablock.com',
    ctaText: 'Explore More',
    themeAccent: '#1a5276',
    isFeatured: false,
    
    subtitle: 'Honoring Black trailblazers who broke barriers and made history in science, exploration, and culture.',
    description: 'The Pioneers lineup pairs custom artwork structures with real-time immersive 3D character nodes natively accessible on web devices.',
    collaborationText: 'A historical tribute series transforming local streetwear options into responsive learning spaces.',
    
    apparel: [
    { name: 'Bessie Coleman', image: '/assets/Images/Pioneers/Coleman_Trans.png' },
    { name: 'Bass Reeves', image: '/assets/Images/Pioneers/Reeves_Trans.png' },
    { name: 'Mary Seacole', image: '/assets/Images/Pioneers/Seacole_Trans.png' },
    { name: 'Jean DuSable', image: '/assets/Images/Pioneers/DuSable_Trans.png' },
    { name: 'Mary Fields', image: '/assets/Images/Pioneers/Mary_Trans.png' },
    { name: 'Matthew Henson', image: '/assets/Images/Pioneers/Henson_Trans.png' }
    ]
  },
  characters: [
    {
      targetIndex: 0,
      characterId: 'seacole',
      name: 'Mary Seacole',
      videoUrl: '/assets/Animations/Seacole/seacole.webm',
      iosVideoUrl: '/assets/Animations/Seacole/seacole.mov',
      chromeIOSVideoUrl: '/assets/Animations/Seacole/seacole.mp4',
      audioUrl: '/assets/music/Pioneers/seacole.m4a',
      thumbnail: '/assets/Images/Pioneers/Seacole_Trans.png',
      mindFile: '/assets/compiled-image-targets/Seacole.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'Nurse and humanitarian who cared for soldiers in the Crimean War'
    },
    {
      targetIndex: 1,
      characterId: 'reeves',
      name: 'Bass Reeves',
      videoUrl: '/assets/Animations/Reeves/reeves.webm',
      iosVideoUrl: '/assets/Animations/Reeves/reeves.mov',
      chromeIOSVideoUrl: '/assets/Animations/Reeves/reeves.mp4',
      audioUrl: '/assets/music/Pioneers/reeves.m4a',
      thumbnail: '/assets/Images/Pioneers/Reeves_Trans.png',
      mindFile: '/assets/compiled-image-targets/Reeves.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'First Black U.S. Marshal west of the Mississippi'
    },
    {
      targetIndex: 2,
      characterId: 'mary',
      name: 'Mary McLeod Bethune',
      videoUrl: '/assets/Animations/Mary/mary.webm',
      iosVideoUrl: '/assets/Animations/Mary/mary.mov',
      chromeIOSVideoUrl: '/assets/Animations/Mary/mary.mp4',
      audioUrl: '/assets/music/Pioneers/mary.m4a',
      thumbnail: '/assets/Images/Pioneers/Mary_Trans.png',
      mindFile: '/assets/compiled-image-targets/Mary.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'Educator and civil rights leader'
    },
    {
      targetIndex: 3,
      characterId: 'henson',
      name: 'Matthew Henson',
      videoUrl: '/assets/Animations/Henson/henson.webm',
      iosVideoUrl: '/assets/Animations/Henson/Henson Animation.mov',
      chromeIOSVideoUrl: '/assets/Animations/Henson/henson.mp4',
      audioUrl: '/assets/music/Pioneers/henson.m4a',
      thumbnail: '/assets/Images/Pioneers/Henson_Trans.png',
      mindFile: '/assets/compiled-image-targets/Henson.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'First person to reach the North Pole'
    },
    {
      targetIndex: 4,
      characterId: 'dusable',
      name: 'Jean Baptiste Point DuSable',
      videoUrl: '/assets/Animations/DuSable/dusable.webm',
      iosVideoUrl: '/assets/Animations/DuSable/dusable.mov',
      chromeIOSVideoUrl: '/assets/Animations/DuSable/dusable.mp4',
      audioUrl: '/assets/music/Pioneers/dusable.m4a',
      thumbnail: '/assets/Images/Pioneers/DuSable_Trans.png',
      mindFile: '/assets/compiled-image-targets/DuSable.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'Founder of Chicago'
    },
    {
      targetIndex: 5,
      characterId: 'coleman',
      name: 'Bessie Coleman',
      videoUrl: '/assets/Animations/Coleman/coleman.webm',
      iosVideoUrl: '/assets/Animations/Coleman/coleman.mov',
      chromeIOSVideoUrl: '/assets/Animations/Coleman/coleman.mp4',
      audioUrl: '/assets/music/Pioneers/coleman.m4a',
      thumbnail: '/assets/Images/Pioneers/Coleman_Trans.png',
      mindFile: '/assets/compiled-image-targets/Coleman.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'First Black woman to hold a pilot\'s license'
    }
  ]
}

// ---------------------------------------------------------------------------
// RockAR Demo Collection
// ---------------------------------------------------------------------------
export const ROCK_AR_DEMO_CLIENT: ClientConfig = {
  clientId: 'rockardemo',
  clientName: 'RockAR Demo',
  enabled: true,
  experienceConfig: {
    experienceId: 'rockar-demo',
    clientName: 'RockAR Demo',
    ctaText: 'Explore More',
    ctaUrl: 'https://rockablock.com',
    triggerAssets: ['/assets/compiled-image-targets/roq-target.mind'],
    themeAccent: '#ff086d',
    aboutText: '',
    aboutTitle: 'RockAR Demo',
    rockablockLink: 'https://rockablock.com'
  },
  experienceItem: {
    id: 'rockar-demo',
    title: 'RockAR Demo',
    thumbnail: '/rockarlogo.png',
    category: 'Brands',
    triggerImage: '/assets/compiled-image-targets/roq-target.mind',
    clientName: 'RockAR Demo',
    ctaUrl: 'https://rockablock.com',
    ctaText: 'Explore More',
    themeAccent: '#ff086d',
    isFeatured: false,
    subtitle: 'A single-character demo collection for ROCK AR.',
    description: 'RockAR Demo provides a lightweight single-character AR collection experience.',
    collaborationText: 'Built as a focused demo flow for fast collection testing and walkthroughs.',
    apparel: [
      { name: 'demo', image: '/rockarlogo.png' },
    ]
  },
  characters: [
    {
      targetIndex: 0,
      characterId: 'demo',
      name: 'demo',
      videoUrl: '/ROQ x ROCKaBLOCK.MOV',
      iosVideoUrl: '/ROQ x ROCKaBLOCK.MOV',
      chromeIOSVideoUrl: '/ROQ x ROCKaBLOCK.MOV',
      thumbnail: '/rockarlogo.png',
      mindFile: '/assets/compiled-image-targets/roq-target.mind',
      ctaUrl: 'https://rockablock.com',
      ctaText: 'Learn More',
      description: 'RockAR demo character'
    }
  ]
}

// ---------------------------------------------------------------------------
// RockABlock (legacy placeholder — kept for backward compat)
// ---------------------------------------------------------------------------
export const ROCKABLOCK_CLIENT: ClientConfig = {
  clientId: 'rockablock',
  clientName: 'RockABlock',
  enabled: false, 
  experienceConfig: {
    experienceId: 'rockablock-ar-experience',
    clientName: 'RockABlock',
    ctaText: 'Visit RockABlock',
    ctaUrl: 'https://rockablock.com',
    triggerAssets: ['/assets/compiled-image-targets/roq-target.mind'],
    themeAccent: '#ff086d',
    aboutText: '',
    aboutTitle: 'About RockABlock AR',
    rockablockLink: 'https://rockablock.com'
  },
  experienceItem: {
    id: 'rockablock-ar-experience',
    title: 'RockABlock AR Experience',
    thumbnail: '/assets/logos/rockablock-logo.jpg',
    category: 'Brands',
    triggerImage: '/assets/compiled-image-targets/roq-target.mind',
    clientName: 'RockABlock',
    ctaUrl: 'https://rockablock.com',
    ctaText: 'Visit RockABlock',
    themeAccent: '#ff086d',
    
    subtitle: 'Explore RockABlock\'s innovative AR experiences.',
    aboutInnerImage: '',
    aboutImageOverlay: '',
    description: 'Explore RockABlock\'s innovative AR experiences.',
    collaborationText: '',
    stats: {
      scans: '0',
      activeUsers: '0',
      engagementRate: '0%'
    }
  }
}
// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
export const CLIENT_CONFIGS: Record<string, ClientConfig> = {
  aana: AANA_CLIENT,
  rockardemo: ROCK_AR_DEMO_CLIENT,
  rockablock: ROCKABLOCK_CLIENT,
  'freedom-fighters': FREEDOM_FIGHTERS_CLIENT,
  pioneers: PIONEERS_CLIENT,
  roq: ROQ_CLIENT,
}

export function getClientConfig(clientId: string): ClientConfig | undefined {
  return CLIENT_CONFIGS[clientId.toLowerCase()]
}

export function getEnabledClients(): ClientConfig[] {
  return Object.values(CLIENT_CONFIGS).filter(client => client.enabled)
}

export function getClientIdFromURL(): string | null {
  const path = window.location.pathname
  const segments = path.split('/').filter(segment => segment.length > 0)
  if (segments.length > 0) {
    return segments[0].toLowerCase()
  }
  return null
}

export function isValidClient(clientId: string): boolean {
  const config = getClientConfig(clientId)
  return config !== undefined && config.enabled
}
